import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  LogOut, Inbox, Phone, Mail, MapPin, Image as ImageIcon,
  Loader2, ClipboardList, Sprout, ArrowRight, X,
  CalendarDays, FileText, Receipt, Users, Repeat, BarChart3,
  Download, CreditCard, Calculator, MapPinned, Wallet, Bell, Star,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, API } from "../lib/api";
import SiteContentEditor from "../components/SiteContentEditor";
import GalleryManager from "../components/GalleryManager";
import ScheduleCalendar from "../components/ops/ScheduleCalendar";
import EstimatesManager from "../components/ops/EstimatesManager";
import InvoicesManager from "../components/ops/InvoicesManager";
import RecurringManager from "../components/ops/RecurringManager";
import MetricsPanel from "../components/ops/MetricsPanel";
import ExportPanel from "../components/ops/ExportPanel";
import CustomersManager from "../components/ops/CustomersManager";
import EmployeeJobs from "../components/ops/EmployeeJobs";
import TestimonialsManager from "../components/ops/TestimonialsManager";
import { ComingSoon, Pill, money } from "../components/ops/common";

function StatusBadge({ status }) {
  const map = {
    new: "bg-gold/15 text-gold-dark",
    contacted: "bg-emerald/10 text-emerald",
    closed: "bg-charcoal/10 text-charcoal/70",
  };
  return <span className={`text-xs font-600 px-2.5 py-1 rounded-sm capitalize ${map[status] || map.new}`}>{status}</span>;
}

function AdminDashboard() {
  const [quotes, setQuotes] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data);
    } catch {
      setQuotes([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    const fd = new FormData();
    fd.append("status", status);
    await api.patch(`/quotes/${id}`, fd);
    load();
  };

  const deleteQuote = async (id) => {
    setDeleting(true);
    try { await api.delete(`/quotes/${id}`); setConfirmDelete(null); load(); }
    catch (e) { console.error("Failed to delete quote", e); }
    finally { setDeleting(false); }
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-2xl font-600 text-charcoal flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-emerald" /> Quote Requests
          </h2>
          <p className="text-olive mt-1">{quotes ? `${quotes.length} total request${quotes.length === 1 ? "" : "s"}` : "Loading…"}</p>
        </div>
      </div>

      {quotes === null ? (
        <div className="flex items-center gap-2 text-olive py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading requests…</div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-20 bg-[#F8F9FA] rounded-sm border border-border" data-testid="admin-empty">
          <Inbox className="w-12 h-12 text-olive/40 mx-auto mb-3" />
          <p className="text-olive">No quote requests yet.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {quotes.map((q) => (
            <div key={q.id} className="bg-white border border-border rounded-sm p-6" data-testid={`quote-row-${q.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-600 text-lg text-charcoal">{q.name}</h3>
                    <StatusBadge status={q.status} />
                  </div>
                  <p className="text-sm text-olive mt-0.5">{new Date(q.created_at).toLocaleString()}</p>
                </div>
                <span className="bg-emerald/10 text-emerald text-sm font-600 px-3 py-1.5 rounded-sm">{q.service}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm mb-4">
                <span className="flex items-center gap-2 text-charcoal/80"><Phone className="w-4 h-4 text-emerald" /> {q.phone}</span>
                <span className="flex items-center gap-2 text-charcoal/80"><Mail className="w-4 h-4 text-emerald" /> {q.email}</span>
                <span className="flex items-center gap-2 text-charcoal/80"><MapPin className="w-4 h-4 text-emerald" /> {q.address}, {q.city}</span>
                <span className="flex items-center gap-2 text-charcoal/80">Prefers: <strong>{q.preferred_contact}</strong></span>
              </div>

              {q.yard_details && <p className="text-olive bg-[#F8F9FA] rounded-sm p-3 text-[15px] mb-4">{q.yard_details}</p>}

              {q.photos?.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-4" data-testid={`quote-photos-${q.id}`}>
                  {q.photos.map((p, i) => (
                    <button key={p.storage_path} onClick={() => setLightbox(`${API}/files/${p.storage_path}`)} className="w-20 h-20 rounded-sm overflow-hidden border border-border hover:opacity-80 transition-opacity">
                      <img src={`${API}/files/${p.storage_path}`} alt={p.original_filename || `photo-${i}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-border items-center">
                {["new", "contacted", "closed"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(q.id, s)}
                    data-testid={`status-${s}-${q.id}`}
                    className={`text-xs font-600 px-3 py-1.5 rounded-sm capitalize transition-colors ${q.status === s ? "bg-emerald text-white" : "bg-[#F8F9FA] text-charcoal/70 hover:bg-border"}`}
                  >
                    Mark {s}
                  </button>
                ))}
                <button onClick={() => setConfirmDelete(q)} data-testid={`delete-quote-${q.id}`} className="ml-auto text-xs font-600 px-3 py-1.5 rounded-sm text-red-600 hover:bg-red-50">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button className="absolute top-5 right-5 text-white"><X className="w-7 h-7" /></button>
          <img src={lightbox} alt="enlarged" className="max-w-full max-h-full object-contain rounded-sm" />
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => !deleting && setConfirmDelete(null)} data-testid="delete-quote-dialog">
          <div className="bg-white rounded-sm w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-600 text-charcoal mb-2">Delete this quote request?</h3>
            <p className="text-olive text-sm mb-1">
              This permanently deletes the request from <strong>{confirmDelete.name}</strong>
              {confirmDelete.photos?.length ? ` and its ${confirmDelete.photos.length} uploaded photo(s)` : ""}.
            </p>
            <p className="text-red-600 text-sm font-600 mb-5">This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} disabled={deleting} data-testid="cancel-delete-btn"
                className="px-5 py-2.5 rounded-sm font-600 text-charcoal/70 hover:bg-[#F8F9FA]">Cancel</button>
              <button onClick={() => deleteQuote(confirmDelete.id)} disabled={deleting} data-testid="confirm-delete-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-600 bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LeaveReviewCard({ user }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", location: "", text: "", rating: 5 });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) { toast.error("Please write a short review first."); return; }
    setSaving(true);
    try {
      await api.post("/testimonials/submit", form);
      toast.success("Thank you! Your review will appear once approved.");
      setDone(true); setOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not submit review."); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm p-6 mb-8" data-testid="leave-review-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-600 text-charcoal flex items-center gap-2"><Star className="w-5 h-5 text-gold fill-gold" /> Enjoyed our work?</h3>
          <p className="text-olive text-sm mt-1 max-w-md">Share a quick review — approved reviews appear on our homepage and help neighbors find us.</p>
        </div>
        {done ? (
          <span className="text-emerald font-600 text-sm" data-testid="review-submitted-msg">Submitted — pending approval. Thank you!</span>
        ) : (
          <button onClick={() => setOpen((v) => !v)} data-testid="leave-review-btn" className="inline-flex items-center gap-2 bg-emerald text-white px-5 py-2.5 rounded-sm font-600 hover:bg-emerald-dark transition-colors">
            <Star className="w-4 h-4" /> Leave a Review
          </button>
        )}
      </div>
      {open && !done && (
        <form onSubmit={submit} className="mt-5 pt-5 border-t border-border space-y-4" data-testid="review-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-600 text-charcoal mb-1">Your name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name" className="w-full border border-border rounded-sm px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-emerald/40" />
            </div>
            <div>
              <label className="block text-sm font-600 text-charcoal mb-1">City (optional)</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="review-location" placeholder="Rincon, GA" className="w-full border border-border rounded-sm px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-emerald/40" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-600 text-charcoal mb-1">Rating</label>
            <div className="flex gap-1" data-testid="review-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}>
                  <Star className={`w-7 h-7 ${n <= form.rating ? "fill-gold text-gold" : "text-charcoal/25"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-600 text-charcoal mb-1">Your review</label>
            <textarea rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text" className="w-full border border-border rounded-sm px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-emerald/40" placeholder="Tell us how we did…" />
          </div>
          <button type="submit" disabled={saving} data-testid="review-submit-btn" className="inline-flex items-center gap-2 bg-gold text-charcoal px-6 py-3 rounded-sm font-700 hover:bg-gold-dark transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Star className="w-5 h-5" />} Submit Review
          </button>
        </form>
      )}
    </div>
  );
}

function CustomerDashboard({ user }) {
  const [quotes, setQuotes] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [props, setProps] = useState([]);

  useEffect(() => {
    api.get("/my/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
    api.get("/estimates").then(({ data }) => setEstimates(data)).catch(() => {});
    api.get("/properties").then(({ data }) => setProps(data)).catch(() => {});
  }, []);

  const approve = async (id, status) => {
    try { await api.put(`/estimates/${id}`, { status }); setEstimates((es) => es.map((e) => e.id === id ? { ...e, status } : e)); }
    catch (e) { console.error("Failed to update estimate", e); }
  };

  return (
    <div data-testid="customer-dashboard">
      <div className="bg-emerald text-white rounded-sm p-8 md:p-10 mb-8">
        <span className="grid place-items-center w-12 h-12 rounded-sm bg-white/15 mb-5"><Sprout className="w-6 h-6" /></span>
        <h2 className="font-display text-2xl sm:text-3xl font-600">Welcome, {user.name?.split(" ")[0] || "neighbor"}!</h2>
        <p className="text-white/80 mt-2 max-w-xl">Track your quotes, estimates, and properties — all in one place.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-border rounded-sm p-6" data-testid="customer-quotes">
          <h3 className="font-600 text-charcoal flex items-center gap-2 mb-4"><FileText className="w-5 h-5 text-emerald" /> My Quote Requests</h3>
          {quotes.length === 0 ? <p className="text-olive text-sm">No quotes yet. <Link to="/quote" className="text-emerald underline">Request one</Link>.</p> : quotes.map((q) => (
            <div key={q.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="text-charcoal text-sm">{q.service} · {(q.created_at || "").slice(0, 10)}</span><Pill status={q.status} />
            </div>
          ))}
        </div>
        <div className="bg-white border border-border rounded-sm p-6" data-testid="customer-estimates">
          <h3 className="font-600 text-charcoal flex items-center gap-2 mb-4"><Receipt className="w-5 h-5 text-emerald" /> My Estimates</h3>
          {estimates.length === 0 ? <p className="text-olive text-sm">No estimates yet.</p> : estimates.map((e) => (
            <div key={e.id} className="py-2 border-b border-border last:border-0">
              <div className="flex items-center justify-between"><span className="text-charcoal text-sm">{e.scope || "Estimate"} · {money(e.total)}</span><Pill status={e.status} /></div>
              {["sent", "draft"].includes(e.status) && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => approve(e.id, "approved")} data-testid={`approve-estimate-${e.id}`} className="text-xs bg-emerald text-white px-3 py-1.5 rounded-sm font-600">Approve</button>
                  <button onClick={() => approve(e.id, "declined")} className="text-xs bg-[#F8F9FA] text-charcoal/70 px-3 py-1.5 rounded-sm font-600">Decline</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm p-6 mb-8" data-testid="customer-properties">
        <h3 className="font-600 text-charcoal flex items-center gap-2 mb-4"><MapPin className="w-5 h-5 text-emerald" /> My Properties</h3>
        {props.length === 0 ? <p className="text-olive text-sm">No properties on file. We'll add yours when we set up service.</p> : props.map((p) => (
          <div key={p.id} className="py-2 border-b border-border last:border-0 text-sm"><span className="font-600 text-charcoal">{p.label}</span> — {p.address}, {p.city}</div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        <ComingSoon title="Pay Online" description="Pay invoices securely by card — coming soon." testid="customer-pay-online" />
        <ComingSoon title="Notification Preferences" description="Choose email or text reminders — coming soon." testid="customer-notif-prefs" />
      </div>

      <LeaveReviewCard user={user} />

      <div className="bg-[#F8F9FA] border border-border rounded-sm p-8 text-center">
        <p className="text-olive mb-4">Need something done?</p>
        <Link to="/quote" className="inline-flex items-center gap-2 bg-emerald text-white px-7 py-3.5 rounded-sm font-600 hover:bg-emerald-dark transition-colors" data-testid="dashboard-quote-btn">
          Request a Quote <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
}

function EmployeeDashboard() {
  const [view, setView] = useState("jobs");
  return (
    <div data-testid="employee-dashboard">
      <div className="flex gap-1 mb-6 border-b border-border">
        {[["jobs", "My Jobs"], ["calendar", "My Calendar"]].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`emp-tab-${k}`}
            className={`px-4 py-3 font-600 text-sm -mb-px border-b-2 transition-colors ${view === k ? "border-emerald text-emerald" : "border-transparent text-charcoal/60 hover:text-charcoal"}`}>{label}</button>
        ))}
      </div>
      {view === "jobs" ? <EmployeeJobs /> : <ScheduleCalendar role="employee" />}
    </div>
  );
}

const ADMIN_TABS = [
  ["leads", "Quote Requests", ClipboardList],
  ["schedule", "Schedule", CalendarDays],
  ["estimates", "Estimates", FileText],
  ["invoices", "Invoices", Receipt],
  ["recurring", "Recurring", Repeat],
  ["customers", "Team & Customers", Users],
  ["metrics", "Metrics", BarChart3],
  ["content", "Edit Website", ImageIcon],
  ["gallery", "Our Work", ImageIcon],
  ["testimonials", "Testimonials", Star],
  ["export", "Export", Download],
  ["payroll", "Payroll", Wallet],
  ["accounting", "Accounting", Calculator],
  ["crew", "Crew Tracking", MapPinned],
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const role = user?.role;
  const isAdmin = role === "admin";
  const isEmployee = role === "employee";
  const [tab, setTab] = useState("leads");

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-73px)]" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-sm tracking-[0.2em] uppercase font-600 text-gold">
              {isAdmin ? "Admin Dashboard" : isEmployee ? "Crew Portal" : "Customer Portal"}
            </span>
            <h1 className="font-display text-3xl font-700 text-charcoal mt-1">Legacy Portal</h1>
          </div>
          <button onClick={logout} data-testid="dashboard-logout-btn" className="inline-flex items-center gap-2 text-charcoal/70 hover:text-emerald font-500">
            <LogOut className="w-5 h-5" /> Log out
          </button>
        </div>

        {isAdmin && (
          <div className="flex gap-1 mb-8 border-b border-border overflow-x-auto" data-testid="admin-tabs">
            {ADMIN_TABS.map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`px-4 py-3 font-600 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-emerald text-emerald" : "border-transparent text-charcoal/60 hover:text-charcoal"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {role === "customer" && <CustomerDashboard user={user} />}
        {isEmployee && <EmployeeDashboard />}
        {isAdmin && tab === "leads" && <AdminDashboard />}
        {isAdmin && tab === "schedule" && <ScheduleCalendar role="admin" />}
        {isAdmin && tab === "estimates" && <EstimatesManager />}
        {isAdmin && tab === "invoices" && <InvoicesManager />}
        {isAdmin && tab === "recurring" && <RecurringManager />}
        {isAdmin && tab === "customers" && <CustomersManager />}
        {isAdmin && tab === "metrics" && <MetricsPanel />}
        {isAdmin && tab === "content" && <SiteContentEditor />}
        {isAdmin && tab === "gallery" && <GalleryManager />}
        {isAdmin && tab === "testimonials" && <TestimonialsManager />}
        {isAdmin && tab === "export" && <ExportPanel />}
        {isAdmin && tab === "payroll" && <ComingSoon title="Payroll" description="Run crew payroll right from your dashboard — coming soon." testid="payroll-soon" />}
        {isAdmin && tab === "accounting" && <ComingSoon title="Accounting" description="Sync with QuickBooks and more — coming soon." testid="accounting-soon" />}
        {isAdmin && tab === "crew" && <ComingSoon title="Crew Tracking" description="Live GPS fleet & crew tracking — coming soon." testid="crew-soon" />}
      </div>
    </div>
  );
}
